import React, { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import logger from "@/lib/logger";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = no user
  const [loading, setLoading] = useState(true);
  // Admin Mode — when an admin disables this toggle, they see the same UI as a
  // regular employee. Persisted in localStorage so the choice survives reloads.
  const [adminMode, setAdminModeRaw] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem("analogy_admin_mode") !== "off";
  });
  const setAdminMode = useCallback((v) => {
    setAdminModeRaw(v);
    try { window.localStorage.setItem("analogy_admin_mode", v ? "on" : "off"); } catch (err) { logger.warn("localStorage write failed (private browsing?)", err); }
  }, []);

  // Public marketing routes (Careers) never need a session, so the /auth/me
  // bootstrap is deferred (see AuthBootstrap) until a route that does.
  const bootstrappedRef = useRef(false);

  const refresh = useCallback(async () => {
    bootstrappedRef.current = true;
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      const status = err?.response?.status;
      // 423 = account in non-active state (e.g. vendor still onboarding). Surface
      // a lightweight user stub so the app can route to /vendor/onboarding etc.
      if (status === 423) {
        try {
          const { data: vme } = await api.get("/vendor/onboarding/me");
          setUser({ ...vme, _pending: true });
        } catch {
          const detail = err?.response?.data?.detail || {};
          setUser({ _pending: true, status: detail.status, _label: detail.label });
        }
      } else {
        if (status !== 401) {
          logger.error("Auth refresh failed:", err?.message || err);
        }
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Mark pending right at login time so we don't briefly render the full app
    // for accounts that aren't activated yet (status !== "active").
    const status = data?.status || "active";
    const isVendor = data?.role === "vendor" || data?.kind === "vendor";
    if (status !== "active" && !isVendor) {
      setUser({ ...data, _pending: true, _label: `Account ${status.replace(/_/g, " ")}` });
    } else {
      setUser(data);
    }
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      logger.error("Logout request failed:", err?.message || err);
    }
    // Feb 2026 · Task #2 — clear per-user client-side prefs (Projects filters
    // etc.) on logout so the next user on this browser doesn't inherit them.
    try {
      Object.keys(localStorage).forEach((k) => {
        if (k.startsWith("projects.filters.")) localStorage.removeItem(k);
      });
    } catch { /* noop */ }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, refresh, bootstrappedRef, formatApiError, adminMode, setAdminMode }),
    [user, loading, login, logout, refresh, adminMode, setAdminMode]
  );

  // Silent sliding renewal — while signed in, quietly re-hit /auth/me every
  // 15 minutes and on tab focus. Each call slides the server-side 8h session
  // (fresh cookie) so active users are never logged out mid-work. A 401/423
  // is already handled globally (redirect to /login), so we only renew when a
  // real user is present and never overlap in-flight pings.
  const renewingRef = useRef(false);
  useEffect(() => {
    if (!user || user === false || user?._pending) return undefined;
    const ping = async () => {
      if (renewingRef.current) return;
      renewingRef.current = true;
      try { await api.get("/auth/me"); }
      catch { /* global 401 handler deals with expiry */ }
      finally { renewingRef.current = false; }
    };
    const timer = setInterval(ping, 15 * 60 * 1000);
    const onFocus = () => ping();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(timer); window.removeEventListener("focus", onFocus); };
  }, [user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
